import {useCallback, useEffect, useState} from 'react'
import debounce from 'lodash/debounce'
import identity from 'lodash/identity'
import {isRefObject, isServer} from '../../utils/utils'
import useEffectEvent from '../../hooks/use-effect-event'
import useAnimationFrameCallback from './useAnimationFrameCallback'

/**
 * Retrieves and tracks the computed value of a CSS custom property (CSS variable).
 * Automatically updates the value on window resize events with debouncing.
 *
 * The hook uses `requestAnimationFrame` for optimal performance and supports inline
 * parser functions without causing unnecessary re-renders.
 *
 * @param {string} varName - The CSS variable name (e.g., '--my-var')
 * @param {Object} [options] - Configuration options
 * @param {HTMLElement|React.RefObject<HTMLElement>} [options.element] - Target element to read the CSS variable from. Defaults to document.documentElement
 * @param {*} [options.initialValue] - Initial value before the first computation
 * @param {Function} [options.parser=identity] - Function to parse the CSS variable value. Receives the raw string value and returns the parsed result. Can be an inline function.
 * @param {number} [options.debounceTimeMs=50] - Debounce time in milliseconds for resize events
 * @returns {[*, Function]} Tuple containing [currentValue, refreshFunction] where refreshFunction manually triggers a value update
 *
 * @example
 * // Basic usage with default element
 * const [spacing] = useCssVarValue('--spacing-large')
 *
 * @example
 * // With custom element and inline parser (no useCallback needed)
 * const [width] = useCssVarValue('--container-width', {
 *   element: containerRef,
 *   parser: (val) => parseInt(val, 10),
 *   initialValue: 0
 * })
 *
 * @example
 * // With manual refresh capability
 * const [headerHeight, refreshHeight] = useCssVarValue('--header-clearance-block-size', {
 *   parser: (val) => parseInt(val, 10)
 * })
 * // Later: manually trigger update
 * useEffect(() => {
 *   refreshHeight() // Re-read CSS variable value
 * }, [someDependency])
 */
const useCssVarValue = (
    varName,
    {
        element: _element = isServer ? null : document.documentElement,
        initialValue,
        parser = identity,
        debounceTimeMs = 50
    } = {}
) => {
    const [value, setValue] = useState(initialValue)

    // handle parser as inline function
    const onParse = useEffectEvent(parser)

    // resolve ref if provided
    const element = isRefObject(_element) ? _element.current : _element

    // perform refresh inside animation frame
    const doRefresh = useAnimationFrameCallback(() => {
        if (!element) return
        const newValue = getComputedStyle(element).getPropertyValue(varName).trim()
        const parsedValue = onParse(newValue)
        setValue(parsedValue)
    })

    // refresh the value in the next available animation frame
    const refresh = useCallback(() => {
        if (!element) return

        doRefresh()
    }, [element, doRefresh])

    // watch the viewport resize and refresh the value
    useEffect(() => {
        if (!element) return

        refresh()
        const onViewportResize = debounce(() => {
            refresh()
        }, debounceTimeMs)

        window.addEventListener('resize', onViewportResize, {passive: true})
        return () => {
            onViewportResize.cancel()
            window.removeEventListener('resize', onViewportResize)
        }
    }, [debounceTimeMs, element, refresh])

    return [value, refresh]
}

export default useCssVarValue
